import toLower from 'lodash/toLower';
import encryptor from '../../lib/utils/encryptor';

const encryptCognitoConnection = ({
  userpoolId,
  userpoolClientId,
  graphqlEndpoint,
  username,
  password,
}) => ({
  userpoolId: encryptor.encrypt(userpoolId),
  userpoolClientId: encryptor.encrypt(userpoolClientId),
  graphqlEndpoint: encryptor.encrypt(graphqlEndpoint),
  username: encryptor.encrypt(username),
  password: encryptor.encrypt(password),
  users: {
    [toLower(username)]: encryptor.encrypt(password),
  },
  authType: 'cognito',
});

const encryptApiConnection = ({ graphqlEndpoint, apiKey }) => ({
  graphqlEndpoint: encryptor.encrypt(graphqlEndpoint),
  apiKey: apiKey ? encryptor.encrypt(apiKey) : null,
  authType: 'apiKey',
});

const encryptLambdaConnection = ({ graphqlEndpoint, authorizationToken }) => ({
  graphqlEndpoint: encryptor.encrypt(graphqlEndpoint),
  authorizationToken: authorizationToken ? encryptor.encrypt(authorizationToken) : null,
  authType: 'lambda',
});

const encryptOktaConnection = ({ name, graphqlEndpoint, oktaServer, userpoolClientId }) => ({
  name,
  graphqlEndpoint: encryptor.encrypt(graphqlEndpoint),
  oktaServer: encryptor.encrypt(oktaServer),
  userpoolClientId: encryptor.encrypt(userpoolClientId),
  authType: 'okta',
});

const encryptOidcConnection = ({ name, graphqlEndpoint, oidcIssuer, oidcClientId, authType, oidcNonce, oidcState }) => ({
  name,
  graphqlEndpoint: encryptor.encrypt(graphqlEndpoint),
  oidcIssuer: encryptor.encrypt(oidcIssuer),
  oidcClientId: encryptor.encrypt(oidcClientId),
  oidcNonce: oidcNonce && encryptor.encrypt(oidcNonce),
  oidcState: oidcState && encryptor.encrypt(oidcState),
  authType,
});

const createConnectionsStore = (name, currentConnection) => {
  localStorage.setItem(
    'connections',
    JSON.stringify({
      [name]: currentConnection,
    })
  );
  return currentConnection;
};

const updateConnection = (storedConnections, name, currentConnection) => {
  const parsedStored = JSON.parse(storedConnections);

  if (!parsedStored[name]) {
    parsedStored[name] = currentConnection;
  } else {
    const combinedUsers = { ...parsedStored[name].users, ...currentConnection.users };
    parsedStored[name].users = combinedUsers;
  }

  localStorage.setItem('connections', JSON.stringify(parsedStored));

  return parsedStored[name];
};

export const saveCognitoConnection = connectionDetails => {
  const { name, userpoolId, userpoolClientId, graphqlEndpoint } = connectionDetails;
  const storedConnections = localStorage.getItem('connections');
  const currentConnection = encryptCognitoConnection(connectionDetails);
  const users = storedConnections
    ? updateConnection(storedConnections, name, currentConnection).users
    : createConnectionsStore(name, currentConnection).users;

  return {
    userpoolId,
    userpoolClientId,
    graphqlEndpoint,
    currentEnv: name,
    users,
    authType: 'cognito',
  };
};

export const saveApiConnection = connectionDetails => {
  const { name, graphqlEndpoint, apiKey } = connectionDetails;
  const storedConnections = JSON.parse(localStorage.getItem('connections'));
  const currentConnection = encryptApiConnection(connectionDetails);

  const updatedConnection = { ...storedConnections, [name]: currentConnection };

  localStorage.setItem('connections', JSON.stringify(updatedConnection));

  return {
    graphqlEndpoint,
    apiKey,
    authType: 'apiKey',
  };
};

export const saveLambdaConnection = connectionDetails => {
  const { name, graphqlEndpoint, authorizationToken } = connectionDetails;
  const storedConnections = JSON.parse(localStorage.getItem('connections'));
  const currentConnection = encryptLambdaConnection(connectionDetails);

  const updatedConnection = { ...storedConnections, [name]: currentConnection };

  localStorage.setItem('connections', JSON.stringify(updatedConnection));

  return {
    graphqlEndpoint,
    authorizationToken,
    authType: 'lambda',
  };
};

export const saveOktaConnection = connectionDetails => {
  const { name, graphqlEndpoint, oktaServer, userpoolClientId } = connectionDetails;
  const storedConnections = JSON.parse(localStorage.getItem('connections'));
  const currentConnection = encryptOktaConnection(connectionDetails);
  const updatedConnection = { ...storedConnections, [name]: currentConnection };
  localStorage.setItem('connections', JSON.stringify(updatedConnection));

  return {
    currentEnv: name,
    graphqlEndpoint,
    oktaServer,
    userpoolClientId,
    authType: 'okta'
  };
};

export const saveTempOktaConnection = connectionDetails => {
  const { name, graphqlEndpoint, oktaServer, userpoolClientId } = connectionDetails;
  const encryptedConnection = encryptOktaConnection(connectionDetails);
  localStorage.setItem('tempConnection', JSON.stringify(encryptedConnection));

  return {
    name,
    currentEnv: name,
    graphqlEndpoint,
    oktaServer,
    userpoolClientId,
    authType: 'okta'
  };
};

export const saveOidcConnection = connectionDetails => {
  const { name, graphqlEndpoint, oidcIssuer, oidcClientId } = connectionDetails;
  const storedConnections = JSON.parse(localStorage.getItem('connections'));
  const currentConnection = encryptOidcConnection(connectionDetails);
  const updatedConnection = { ...storedConnections, [name]: currentConnection };
  localStorage.setItem('connections', JSON.stringify(updatedConnection));

  return {
    currentEnv: name,
    graphqlEndpoint,
    oidcIssuer,
    oidcClientId,
    authType: 'oidc'
  };
};

export const saveTempOidcConnection = connectionDetails => {
  const { name, graphqlEndpoint, oidcIssuer, oidcClientId, authType, oidcNonce, oidcState } = connectionDetails;
  const encryptedConnection = encryptOidcConnection(connectionDetails);
  localStorage.setItem('tempConnection', JSON.stringify(encryptedConnection));

  return {
    name,
    currentEnv: name,
    graphqlEndpoint,
    oidcIssuer,
    oidcClientId,
    oidcNonce,
    oidcState,
    authType,
  };
};

export const getStoredConnections = () => JSON.parse(localStorage.getItem('connections'));
export const getTempConnection = () => JSON.parse(localStorage.getItem('tempConnection'));

export const removeConnection = (connectionName) => {
  const storedConnections = JSON.parse(localStorage.getItem('connections'));
  delete storedConnections[connectionName];
  localStorage.setItem('connections', JSON.stringify(storedConnections));
};